import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
    CheckCircleIcon,
    ArrowDownTrayIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    RocketLaunchIcon
} from '@heroicons/react/24/solid';
import useApiWithAuth from '../utils/useApiWithAuth';
import { useToast } from '../components/ToastProvider';

const PremiumSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { apiCall } = useApiWithAuth();
    const toast = useToast();
    
    const sessionId = searchParams.get('session_id');
    const analysisId = searchParams.get('analysisId');
    const serviceType = searchParams.get('serviceType');
    const [serviceData, setServiceData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [missingParams, setMissingParams] = useState(false);

    // Initialisation : pour le pack complet, on définit directement les URLs sans appel API
    useEffect(() => {
        if (!analysisId || !serviceType) {
            setMissingParams(true);
            setIsLoading(false);
            return;
        }

        setMissingParams(false);

        // Pour le pack complet, pas besoin d'appel API
        if (serviceType === 'complete-pack') {
            setServiceData({
                type: 'complete-pack',
                cvUrl: `/api/premium/download/cv-reformatted/${analysisId}`,
                letterUrl: `/api/premium/download/cover-letter/${analysisId}`
            });
            setIsLoading(false);
            return;
        }

        // Pour les autres services, on fait un appel API
        const fetchData = async () => {
            try {
                setIsLoading(true);
                
                const response = await apiCall(
                    `https://cvanalyzerback.osc-fr1.scalingo.io/api/premium/download/${serviceType}/${analysisId}`
                );
                
                // Si c'est un fichier binaire (Word), on crée un lien de téléchargement
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/vnd.openxmlformats')) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    setServiceData({
                        downloadUrl: url,
                        isBlob: true
                    });
                } else {
                    const data = await response.json();
                    setServiceData(data);
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du service:', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [analysisId, serviceType, apiCall]);

    const serviceNames = {
        'cv-reformatted': 'CV Reformé Professionnel',
        'cover-letter': 'Lettre de Motivation IA',
        'complete-pack': 'Pack Complet'
    };

    const serviceIcons = {
        'cv-reformatted': DocumentTextIcon,
        'cover-letter': EnvelopeIcon,
        'complete-pack': RocketLaunchIcon
    };

    const Icon = serviceType ? serviceIcons[serviceType] : DocumentTextIcon;

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 pt-16 p-4">
            <Helmet>
                <title>Service Premium — CV Analyse</title>
            </Helmet>

            <div className="max-w-2xl mx-auto">
                <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 text-center">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircleIcon className="w-12 h-12 text-green-600" />
                    </div>

                    <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                        ✅ Paiement réussi !
                    </h1>

                    <p className="text-xl text-gray-600 mb-8">
                        {missingParams
                            ? 'Nous avons bien reçu votre paiement.'
                            : 'Votre service premium est en cours de génération'}
                    </p>

                    {isLoading ? (
                        <div className="py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Génération en cours...</p>
                        </div>
                    ) : missingParams ? (
                        <div className="py-6 space-y-4 text-left max-w-md mx-auto">
                            <p className="text-gray-600 text-sm leading-relaxed">
                                Le lien de cette page est incomplet (identifiant d&apos;analyse ou type de
                                service manquant). Vos fichiers sont en général disponibles sous{' '}
                                <strong>Mes services</strong> dès que la génération est terminée.
                            </p>
                            {sessionId && (
                                <p className="text-xs text-gray-500 break-all">
                                    Référence Stripe :{' '}
                                    <span className="font-mono bg-gray-100 px-1 rounded">{sessionId}</span>
                                </p>
                            )}
                            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                                <Link
                                    to="/premium/my-services"
                                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all text-center text-sm"
                                >
                                    Mes services premium
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => navigate('/upload')}
                                    className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-800 font-semibold px-6 py-3 rounded-xl hover:bg-gray-200 transition-all text-sm"
                                >
                                    Retour à l&apos;analyse
                                </button>
                            </div>
                        </div>
                    ) : serviceData ? (
                        <div className="space-y-6">
                            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
                                <div className="flex items-center justify-center mb-4">
                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                                        <Icon className="w-8 h-8 text-blue-600" />
                                    </div>
                                </div>
                                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                                    {serviceNames[serviceType] || 'Service Premium'}
                                </h2>
                                <p className="text-gray-600">
                                    {serviceType === 'complete-pack' 
                                        ? 'Vos fichiers sont prêts à être téléchargés'
                                        : 'Votre service est prêt à être téléchargé'}
                                </p>
                            </div>

                            {serviceType === 'complete-pack' ? (
                                <div className="space-y-4">
                                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const response = await apiCall(
                                                        `https://cvanalyzerback.osc-fr1.scalingo.io${serviceData.cvUrl}`
                                                    );
                                                    const blob = await response.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `CV_Reforme_${Date.now()}.docx`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                    window.URL.revokeObjectURL(url);
                                                } catch (error) {
                                                    console.error('Erreur téléchargement CV:', error);
                                                    toast.error(
                                                        'Téléchargement',
                                                        'Impossible de télécharger le CV. Réessayez dans un instant.'
                                                    );
                                                }
                                            }}
                                            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-8 py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
                                        >
                                            <DocumentTextIcon className="w-5 h-5" />
                                            Télécharger le CV
                                        </button>
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const response = await apiCall(
                                                        `https://cvanalyzerback.osc-fr1.scalingo.io${serviceData.letterUrl}`
                                                    );
                                                    const blob = await response.blob();
                                                    const url = window.URL.createObjectURL(blob);
                                                    const a = document.createElement('a');
                                                    a.href = url;
                                                    a.download = `Lettre_Motivation_${Date.now()}.docx`;
                                                    document.body.appendChild(a);
                                                    a.click();
                                                    document.body.removeChild(a);
                                                    window.URL.revokeObjectURL(url);
                                                } catch (error) {
                                                    console.error('Erreur téléchargement lettre:', error);
                                                    toast.error(
                                                        'Téléchargement',
                                                        'Impossible de télécharger la lettre. Réessayez dans un instant.'
                                                    );
                                                }
                                            }}
                                            className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold px-8 py-4 rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                                        >
                                            <EnvelopeIcon className="w-5 h-5" />
                                            Télécharger la lettre
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => navigate('/upload')}
                                        className="w-full inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 font-semibold px-8 py-4 rounded-xl hover:bg-gray-200 transition-all"
                                    >
                                        Nouvelle analyse
                                    </button>
                                </div>
                            ) : (
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <a
                                        href={serviceData.isBlob ? serviceData.downloadUrl : `https://cvanalyzerback.osc-fr1.scalingo.io${serviceData.downloadUrl}`}
                                        download={serviceData.isBlob}
                                        className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-8 py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
                                    >
                                        <ArrowDownTrayIcon className="w-5 h-5" />
                                        Télécharger maintenant
                                    </a>
                                    <button
                                        onClick={() => navigate('/upload')}
                                        className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 font-semibold px-8 py-4 rounded-xl hover:bg-gray-200 transition-all"
                                    >
                                        Nouvelle analyse
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="py-12">
                            <p className="text-gray-600 mb-4">
                                Le service sera disponible dans quelques instants.
                            </p>
                            <p className="text-sm text-gray-500">
                                Vous recevrez un email avec le lien de téléchargement.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PremiumSuccess;

